import { useQuery, skipToken, type UseQueryResult } from "@tanstack/react-query";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { reportItemQuery } from "@visioni/a3s-reports/app";
import {
  WasteSensePayload,
  GetReportResponse,
  getReportEndpoint,
  type ReportItem,
} from "@visioni/a3s-reports/client-messages";
import type { ISODate } from "@visioni/a3s-codecs";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * Distinguishes the two readiness states the backend exposes (design §6.3):
 *  - `not_found` (404) — no row for `(tenant, type, periodStart)`
 *  - `not_ready` (409) — row exists but `generating` / `failed`; the
 *    underlying status is carried in `.status` so the UI can switch between
 *    "still working" and "retry" copy.
 *
 * `queryFor`'s default `queryFn` collapses both cases into a generic
 * `Error("<message>")`, which is fine for most endpoints but loses the
 * signal we need here. We run our own queryFn (still backed by the same
 * endpoint definition for URL/key/schema) and surface them as typed errors.
 */
export class ReportNotFoundError extends Error {
  readonly kind = "not_found" as const;
  constructor(message = "Report not found") {
    super(message);
    this.name = "ReportNotFoundError";
  }
}

export class ReportNotReadyError extends Error {
  readonly kind = "not_ready" as const;
  readonly status: "generating" | "failed";
  constructor(status: "generating" | "failed", message = "Report not ready") {
    super(message);
    this.name = "ReportNotReadyError";
    this.status = status;
  }
}

const STALE_TIME_MS = 60_000;

export interface UseWasteSenseReportResult {
  readonly report: ReportItem | undefined;
  readonly payload: WasteSensePayload | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
}

/**
 * Fetches a single WasteSense report and decodes its plugin-owned payload at
 * the boundary.
 *
 * WasteSense deliberately stays on the legacy `(type, periodStart)` key and is
 * its only consumer (see `client-messages/get-report.ts`), keeping that
 * coupling contained here rather than in shared code.
 *
 * The endpoint returns a short-lived presigned `payloadUrl` rather than
 * inlining the payload (a busy tenant's `payload.json` can exceed Lambda's 6 MB
 * response cap — design §8); we follow it with a second GET to S3 and narrow
 * the JSON once so consumers work against the typed shape.
 */
export function useWasteSenseReport(periodStart: ISODate | undefined): UseWasteSenseReportResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  // Reuse the endpoint's query key so cache invalidation lines up with any
  // future writes that go through `mutationFor`. The default queryFn from
  // `queryFor` is discarded — see class comment above.
  const keyOptions =
    periodStart !== undefined ?
      reportItemQuery(apiConfig, { type: "wastesense", periodStart })
    : { queryKey: [] as const };

  const query = useQuery({
    queryKey: keyOptions.queryKey,
    queryFn:
      apiConfig.tenant && periodStart !== undefined ?
        ({ signal }) => fetchReportWithPayload(apiConfig, periodStart, signal)
      : skipToken,
    enabled: !!tenantId && periodStart !== undefined,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      // 404/409 are terminal for this `periodStart` — retrying won't change
      // the answer. Other errors keep the default backoff (3 attempts).
      if (error instanceof ReportNotFoundError) return false;
      if (error instanceof ReportNotReadyError) return false;
      return failureCount < 3;
    },
  }) as UseQueryResult<FetchReportResult, Error>;

  const reportItem = query.data?.response.data;
  const payload = query.data ? decodePayload(query.data.payload) : undefined;

  return {
    report: reportItem,
    payload,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    isTenantPending: isTenantLoading || !tenantId,
  };
}

/**
 * Bundles the typed metadata envelope with the raw payload JSON fetched from
 * the presigned URL, so React Query caches both under the report's key and a
 * cache hit needs no second network round-trip.
 */
interface FetchReportResult {
  readonly response: GetReportResponse;
  readonly payload: unknown;
}

async function fetchReportWithPayload(
  apiConfig: ApiConfig,
  periodStart: ISODate,
  signal: AbortSignal | undefined,
): Promise<FetchReportResult> {
  const response = await fetchReport(apiConfig, periodStart, signal);
  const payload = await fetchPayload(response.data.payloadUrl, signal);
  return { response, payload };
}

/**
 * Follows the presigned `payloadUrl` with a plain GET. No auth header: the
 * URL is SigV4-presigned, and attaching an `Authorization` header alongside
 * the query-string signature makes S3 reject it.
 */
async function fetchPayload(payloadUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const response = await fetch(payloadUrl, signal ? { signal } : {});
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Payload fetch failed: ${String(response.status)}`);
  }
  return response.json();
}

async function fetchReport(
  apiConfig: ApiConfig,
  periodStart: ISODate,
  signal: AbortSignal | undefined,
): Promise<GetReportResponse> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    // The hook gates on `enabled`/`skipToken` so this is unreachable in
    // practice, but the type system doesn't know that.
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getReportEndpoint, {
    path: { tenantId: tenant.tenantId, type: "wastesense", periodStart },
  });
  const url = `${tenant.apiBaseUrl}${path}`;
  const response = await authenticatedFetch(url, signal ? { signal } : {});
  if (response.status === 404) {
    throw new ReportNotFoundError();
  }
  if (response.status === 409) {
    const status = await readNotReadyStatus(response);
    throw new ReportNotReadyError(status);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed: ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  return GetReportResponse.parse(body);
}

async function readNotReadyStatus(response: Response): Promise<"generating" | "failed"> {
  try {
    const body = (await response.json()) as { details?: { status?: unknown } };
    const status = body.details?.status;
    if (status === "generating" || status === "failed") return status;
  } catch {
    // Body wasn't JSON or was missing `details.status`. Default to
    // "generating" — the safer copy ("still working") rather than "failed".
  }
  return "generating";
}

function decodePayload(payload: unknown): WasteSensePayload | undefined {
  const result = WasteSensePayload.safeParse(payload);
  if (result.success) return result.data;
  // Backend validates payloads on write, so this only fires on schema drift.
  // Returning `undefined` lets the renderer show an explicit
  // "payload schema mismatch" state rather than crashing.
  console.error("WasteSensePayload schema mismatch", result.error);
  return undefined;
}
